import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Search } from 'lucide-react'

import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'

function StudentSelect({ students, value, onChange }) {
  const [open, setOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  const filteredStudents = useMemo(() => {
    if (!searchTerm) return students
    const term = searchTerm.toLowerCase()
    return students.filter(
      (student) =>
        student.fullName.toLowerCase().includes(term) || student.skillLevel.toLowerCase().includes(term)
    )
  }, [students, searchTerm])

  const selectedStudent = students.find((s) => s.id === value)

  function handleSelect(studentId) {
    onChange(studentId)
    setOpen(false)
    setSearchTerm('')
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between mt-1">
          {selectedStudent ? selectedStudent.fullName : 'Select a student...'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-2" align="start">
        <div className="flex items-center border-b pb-2 mb-2">
          <Search className="h-4 w-4 text-gray-400 mr-2" />
          <Input
            placeholder="Search students..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border-0 p-0 h-8 focus-visible:ring-0"
          />
        </div>
        <div className="max-h-64 overflow-y-auto">
          {filteredStudents.length === 0 ? (
            <p className="text-sm text-gray-500 py-2 text-center">No students found</p>
          ) : (
            filteredStudents.map((student) => (
              <div
                key={student.id}
                className={`flex items-center px-2 py-2 cursor-pointer rounded hover:bg-gray-100 ${
                  value === student.id ? 'bg-purple-50' : ''
                }`}
                onClick={() => handleSelect(student.id)}
              >
                <div className="flex-1">
                  <div className="font-medium text-sm">{student.fullName}</div>
                  <div className="text-xs text-gray-500">{student.skillLevel}</div>
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

StudentSelect.propTypes = {
  students: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      fullName: PropTypes.string.isRequired,
      skillLevel: PropTypes.string
    })
  ).isRequired,
  value: PropTypes.number,
  onChange: PropTypes.func.isRequired
}

export default StudentSelect
